// The shared map base every surface builds on — client tracking, rider nav,
// therapist pre-pickup card (data-wall scoped by the CALLER), admin ops map.
// Owns the cross-cutting concerns so they can't drift per surface: brand
// style + light/dark reactivity, service-area maxBounds, load timeout →
// error + retry, reduced motion, and full teardown on unmount. Callers own
// markers/routes/overlays via react-map-gl children or `onMapLoad`.
//
// Bundle note: this module statically imports mapbox-gl — never re-export it
// from the @bednbody/ui root. Consume via `@bednbody/ui/service-map` inside a
// React.lazy'd screen so map code stays out of non-map routes (payment, KYC,
// legal, admin login must never pull it).

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import Map, { type MapRef } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { ACTIVE_SERVICE_AREA, MAPBOX_STYLE_STANDARD, type ServiceArea } from "@bednbody/config/map";
import {
  addServiceAreaLayers,
  applyBrandStyle,
  brandBasemapConfig,
  type AreaFeature,
  type MapPresentation,
} from "./brandStyle.js";
import { prefersReducedMotion } from "./interpolate.js";
import { useMapDark } from "../lib/map-style.js";

/** How long the STYLE may take to arrive before we show the error state.
 *  Tiles are not waited on: Mapbox Standard's full first load (3D buildings,
 *  trees, lighting) runs past 10 s, and the old wait on Mapbox's `load`
 *  event turned every map into "Map couldn't load" (2026-10-22). */
const LOAD_TIMEOUT_MS = 9000;

function isDarkTheme(): boolean {
  return typeof document !== "undefined" && document.documentElement.classList.contains("dark");
}

/** Reactive `.dark`-class watcher — theme-dependent map paint lives on this. */
export function useDarkTheme(): boolean {
  const [dark, setDark] = useState(isDarkTheme);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(isDarkTheme()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

export interface ServiceMapProps {
  /** Mapbox public token — each app passes its own import.meta.env.VITE_MAPBOX_TOKEN. */
  token: string | undefined;
  initialViewState?: { longitude: number; latitude: number; zoom: number };
  /** Panning is fenced to this area (defaults to the pilot service area). */
  serviceArea?: ServiceArea;
  /** Set false for tiny embedded maps where panning away makes no sense. */
  constrainToArea?: boolean;
  /** Optional coverage ring / boundary drawn in brand teal. */
  areaOutline?: AreaFeature;
  /** Rider guidance needs stronger road hierarchy than tracking/discovery maps.
      This is deliberately scoped so client, therapist, and admin surfaces do
      not inherit active-navigation styling. */
  presentation?: MapPresentation;
  /** Forces the day/night light instead of following the `.dark` theme —
      the rider's drive map switches by time of day, not by app theme. */
  dark?: boolean;
  interactive?: boolean;
  /**
   * Where Mapbox draws its logo. Default bottom-left; a surface with a panel
   * docked on the left moves it so the logo is not hidden underneath (the
   * logo must stay visible — only the per-app dim/hide policy may touch it).
   */
  logoPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  /** Caller MUST size the wrapper (explicit height) — the map fills it. */
  className?: string;
  style?: CSSProperties;
  /** Raw instance for imperative work (markers, fitBounds, sources). */
  onMapLoad?: (map: MapboxMap) => void;
  /** Loading overlay (e.g. Benny thinking). Default: pulsing skeleton. */
  renderLoading?: () => ReactNode;
  /** Error state (e.g. Benny oops + retry). Default: quiet fallback + retry. */
  renderError?: (retry: () => void) => ReactNode;
  /** react-map-gl children: <Marker/>, <Source/>, <Layer/>, controls… */
  children?: ReactNode;
}

export function ServiceMap({
  token,
  initialViewState,
  serviceArea = ACTIVE_SERVICE_AREA,
  constrainToArea = true,
  areaOutline,
  presentation = "default",
  dark: darkOverride,
  interactive = true,
  logoPosition,
  className,
  style,
  onMapLoad,
  renderLoading,
  renderError,
  children,
}: ServiceMapProps) {
  const mapRef = useRef<MapRef | null>(null);
  const loadedRef = useRef(false);
  const onMapLoadRef = useRef(onMapLoad);
  onMapLoadRef.current = onMapLoad;

  // The person's map setting (Light / Dark / Auto), not the app theme —
  // Leon, 2026-10-23. A caller can still force one with `dark`.
  const mapDark = useMapDark();
  const dark = darkOverride ?? mapDark;
  const darkRef = useRef(dark);
  darkRef.current = dark;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0); // key — remount = retry

  // A style that never finishes loading (offline, blocked token) must not
  // strand the user on a skeleton forever.
  useEffect(() => {
    if (loaded || failed) return;
    const t = window.setTimeout(() => {
      if (!loadedRef.current) setFailed(true);
    }, LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [loaded, failed, attempt]);

  const reduced = useMemo(() => prefersReducedMotion(), []);

  // Brand colours and light preset go in at creation (react-map-gl passes
  // unknown props straight to the Mapbox constructor), so the first frame is
  // already ours. Rebuilt per retry; later day/night changes go through
  // applyBrandStyle.
  const creationConfig = useMemo(
    () => ({ config: { basemap: brandBasemapConfig(darkRef.current, presentation) } }) as object,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- creation-time only
    [attempt],
  );

  // The map works once its style has arrived: show it and hand it to the
  // caller then, while tiles stream in, instead of a skeleton until every
  // first tile has drawn. Idempotent — Mapbox's `load` calls it again.
  function markReady(map: MapboxMap): void {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoaded(true);
    brandPass(map);
    onMapLoadRef.current?.(map);
  }

  function brandPass(map: MapboxMap): void {
    applyBrandStyle(map, darkRef.current, presentation);
    if (areaOutline) addServiceAreaLayers(map, areaOutline);
  }

  // Day ↔ night is Standard's light preset, not a new style: no style load
  // follows a theme or time-of-day switch, so re-apply the pass here.
  useEffect(() => {
    const map = mapRef.current?.getMap() as MapboxMap | undefined;
    if (map && loadedRef.current) brandPass(map);
    // brandPass reads latest-value refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark]);

  function retry(): void {
    loadedRef.current = false;
    setLoaded(false);
    setFailed(false);
    setAttempt((n) => n + 1);
  }

  if (!token) {
    // Misconfiguration, not a runtime blip — say so instead of spinning.
    return (
      <div className={className} style={style}>
        <div className="grid h-full w-full place-items-center rounded-[inherit] border border-dashed border-border bg-muted/40 px-4 text-center text-xs text-muted-foreground">
          Map unavailable — missing map token.
        </div>
      </div>
    );
  }

  if (failed) {
    return (
      <div className={className} style={style}>
        {renderError ? (
          renderError(retry)
        ) : (
          <div className="grid h-full w-full place-items-center rounded-[inherit] border border-dashed border-border bg-muted/40 px-4 text-center">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Map couldn’t load (offline?).</p>
              <button
                type="button"
                onClick={retry}
                className="rounded-full bg-card px-4 py-2 text-xs font-bold text-primary shadow-card ring-1 ring-border/70 transition hover:bg-muted"
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className} style={{ position: "relative", ...style }}>
      <Map
        key={attempt}
        ref={mapRef}
        mapboxAccessToken={token}
        initialViewState={
          initialViewState ?? {
            longitude: serviceArea.center.lng,
            latitude: serviceArea.center.lat,
            zoom: serviceArea.defaultZoom,
          }
        }
        mapStyle={MAPBOX_STYLE_STANDARD}
        maxBounds={
          constrainToArea
            ? (serviceArea.bounds as unknown as [[number, number], [number, number]])
            : undefined
        }
        // Rider nav wants a steeper first-person pitch than Mapbox's 60°
        // default cap allows. Scoped to this presentation only — client,
        // therapist, and admin surfaces keep the default cap.
        maxPitch={presentation === "rider-navigation" ? 75 : undefined}
        interactive={interactive}
        logoPosition={logoPosition}
        // ⚠ Never `undefined`. react-map-gl hands it to Mapbox as an explicit
        // value, which breaks the label fade the first time a GeoJSON source
        // updates — every street, place and hub name then stays invisible.
        // /live-map polls trips every few seconds and showed no map text at
        // all until 2026-10-03. 300 ms is Mapbox's own default.
        fadeDuration={reduced ? 0 : 300}
        style={{ width: "100%", height: "100%" }}
        {...creationConfig}
        onLoad={(e) => {
          const map = e.target as MapboxMap;
          markReady(map);
          // Fully drawn: one more pass for anything added while tiles loaded.
          brandPass(map);
        }}
        // Fires on style changes, layers added later included — re-apply the
        // brand pass (their emissive strength) + runtime sources. Idempotent.
        onStyleData={(e) => {
          // `in`, not `e.target`: the app builds resolve a react-map-gl event
          // type with no `target` on style data, and the build broke on it.
          const fromEvent = "target" in e ? (e.target as MapboxMap) : undefined;
          const map = fromEvent ?? (mapRef.current?.getMap() as MapboxMap | undefined);
          if (!map) return;
          if (!loadedRef.current) markReady(map);
          else brandPass(map);
        }}
        onError={() => {
          // Post-load tile hiccups self-heal; only a load that never happened
          // is fatal.
          if (!loadedRef.current) setFailed(true);
        }}
      >
        {children}
      </Map>

      {!loaded ? (
        <div className="absolute inset-0 z-10">
          {renderLoading ? (
            renderLoading()
          ) : (
            <div className="h-full w-full animate-pulse rounded-[inherit] bg-muted/60" />
          )}
        </div>
      ) : null}
    </div>
  );
}
